import React, { useState } from "react";
import { Form, Switch, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "hooks";
import { Checkbox, Input, Select, Typography } from "@atoms";
import RemoveIcon from "@assets/icons/remove-red.svg";
import HelpIcon from "@assets/icons/help.svg";

import {
  AddPeopleContentWrapper,
  DividerVertical, SwitchText, SwitchTooltip, SwitchWrapper,
} from "../addPeople.styles";
import { DeleteModal } from "@molecules";
import StoryImageUpload from "./storyImageUpload";
import { useStoryFormContext } from "contexts/StoryFormContext";
import DateSelector from "components/atoms/DateSelector/dateSelector";
import { animalStatus, statuses } from "../addPeople.constants";

interface IAddPeopleContent {
  handleDeleteStory: () => void;
}

function AddPeopleContent({ handleDeleteStory }: IAddPeopleContent) {
  const { t } = useTranslation();
  const isMobile = useIsMobile(767);
  const {formData, updateFormData} = useStoryFormContext();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const updateBirthDate = (d: number | null, m: number | null, y: number | null) => {
    updateFormData({
      date_of_birth_day: d,
      date_of_birth_month: m,
      date_of_birth_year: y,
    });
  };

  const updateDeathDate = (d: number | null, m: number | null, y: number | null) => {
    updateFormData({
      date_of_death_day: d,
      date_of_death_month: m,
      date_of_death_year: y,
    });
  };

  const onIsAliveChange = () => {
    if (!formData.is_alive) {
      updateDeathDate(null, null, null);
    }
    updateFormData({
      is_alive: !formData.is_alive
    });
  }

  const isEdit = !!formData?.uuid;

  return (
    <AddPeopleContentWrapper>
      <StoryImageUpload />

      <Form
        style={{ width: "100%", minWidth: '300px' }}
        className="add-people-form"
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          className="responsive-form-item"
          label={
            <label>
              {t("name")}
              <span style={{ color: "#C93636" }}>*</span>
            </label>
          }
          name="username"
        >
          <Input
            onChange={(e) => updateFormData({
              name: e
            })}
            defaultValue={formData.name}
            placeholder={t("writeName")}
          />
        </Form.Item>

        <Form.Item
          className="responsive-form-item date-selectors-add-story-label"
          label={t("dateofbirth")}
          name="birthday"
        >
          <DateSelector
            separate
            initialDay={formData.date_of_birth_day}
            initialMonth={formData.date_of_birth_month}
            initialYear={formData.date_of_birth_year}
            onChange={updateBirthDate}
            showLabels
          />
        </Form.Item>

        {!formData.is_alive && (
          <Form.Item
            className="responsive-form-item date-selectors-add-story-label"
            label={t("dateofdeath")}
            name="diedday"
          >
            <DateSelector
              separate
              initialDay={formData.date_of_death_day}
              initialMonth={formData.date_of_death_month}
              initialYear={formData.date_of_death_year}
              onChange={updateDeathDate}
              showLabels
            />
          </Form.Item>
        )}

        <Checkbox
          checked={formData.is_alive}
          onChange={onIsAliveChange}
          label={t("untilNow")}
          style={{
            alignItems: "center",
            marginTop: formData.is_alive ? 0 : 12,
          }}
        />

        <Form.Item label={t("placeOfBirth")} name="place">
          <Input
            onChange={(e) => updateFormData({
              place_of_birth: e
            })}
            defaultValue={formData.place_of_birth}
            placeholder={t("enterName")}
          />
        </Form.Item>

        <Form.Item
          label={
            <label>
              {t("status")}
              <span style={{ color: "#C93636" }}>*</span>
            </label>
          }
          name="status"
        >
          <Select
            value={t("status")}
            placeholder={t("status")}
            defaultValue={t("relative")}
            // onChange={(e) => updateFormData({
            //   status: Number(e) as 1 | 2 | 3
            // })}
            onChange={(e) => null}
            options={
              window.location.hostname.includes("animals")
                ? animalStatus
                : statuses
            }
          />
        </Form.Item>
      </Form>

      {!isMobile && <DividerVertical />}

      <Form
        style={{ width: "100%" }}
        layout="vertical"
        className="add-people-form"
        requiredMark={false}
      >
        <Form.Item label={t("description")} name="description">
          <Input.TextArea
            defaultValue={formData.description}
            maxLength={2000}
            onChange={(e) => updateFormData({
              description: e
            })}
            showCount
            placeholder={t("enterDescription")}
            style={{ minHeight: "130px" }}
          />
        </Form.Item>

        <Typography as="label">{t("additionally")}</Typography>

        <Form.Item name="display">
          <SwitchWrapper
            onClick={() => updateFormData({
              show_in_feed: !formData.show_in_feed
            })}
          >
            <SwitchText>
              {t("displayFeed")}
              <Tooltip
                trigger={['hover', 'click']}
                title="Добавить историю в общую ленту для всех"
                placement="top"
                getPopupContainer={() => document.body}
                styles={{ root: {zIndex: 100001} }}
              >
                <SwitchTooltip onClick={e => e.stopPropagation()}>
                  <img src={HelpIcon} alt="help"/>
                </SwitchTooltip>
              </Tooltip>
            </SwitchText>
            <Switch style={{width: "auto"}} checked={formData.show_in_feed} />
          </SwitchWrapper>
        </Form.Item>

        {!formData.is_alive && (
          <Form.Item name="cryonized">
            <SwitchWrapper
              onClick={() => updateFormData({
                is_cryonized: !formData.is_cryonized
              })}
            >
              <SwitchText>
                {t("cryonized")}
                <Tooltip
                  trigger={['hover', 'click']}
                  title={
      <a 
        href="https://kriorus.ru/" 
        target="_blank" 
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        style={{textDecoration: 'none', color: '#fff'}}
      >
        https://kriorus.ru/
      </a>
    }
                  placement="top"
                  getPopupContainer={() => document.body}
                  styles={{ root: {zIndex: 100001} }}
                >
                  <SwitchTooltip onClick={e => e.stopPropagation()}>
                    <img src={HelpIcon} alt="help"/>
                  </SwitchTooltip>
                </Tooltip>
              </SwitchText>
              <Switch style={{width: "auto"}} checked={formData.is_cryonized}/>
            </SwitchWrapper>
          </Form.Item>
        )}

        {!formData.is_alive && (
          <Form.Item name="tributes">
            <SwitchWrapper
              onClick={() => updateFormData({
                enable_tribute: !formData.enable_tribute
              })}
            >
              <SwitchText>
                {t("includeTributes")}
                <Tooltip
                  trigger={['hover', 'click']}
                  title="Позволяет получать слова памяти и поддержки от пользователей"
                  placement="top"
                  getPopupContainer={() => document.body}
                  styles={{ root: {zIndex: 100001} }}
                >
                  <SwitchTooltip onClick={e => e.stopPropagation()}>
                    <img src={HelpIcon} alt="help" />
                  </SwitchTooltip>
                </Tooltip>
              </SwitchText>
              <Switch style={{ width: "auto" }} checked={formData.enable_tribute} />
            </SwitchWrapper>
          </Form.Item>
        )}
        {/*{!formData.is_alive && (*/}
        {/*  <Form.Item name="memories">*/}
        {/*    <div*/}
        {/*      // @ts-ignore*/}
        {/*      onClick={() => updateFormData({*/}
        {/*        enable_memories: !formData.enable_memories*/}
        {/*      })}*/}
        {/*      style={{*/}
        {/*        display: "flex",*/}
        {/*        justifyContent: "space-between",*/}
        {/*        width: "100%",*/}
        {/*        cursor: "pointer",*/}
        {/*      }}*/}
        {/*    >*/}
        {/*      <Typography*/}
        {/*        style={{ display: "flex", alignItems: "center", gap: "8px", background: "inherit" }}*/}
        {/*      >*/}
        {/*        {t("turnMemories")}*/}
        {/*        <Tooltip*/}
        {/*          title="Позволяет пользователям делиться своими историями и фото"*/}
        {/*          placement="bottomLeft"*/}
        {/*        >*/}
        {/*          <img src={HelpIcon} alt="help" />*/}
        {/*        </Tooltip>*/}
        {/*      </Typography>*/}
        {/*      <Switch style={{ width: "auto" }} checked={formData.enable_memories} defaultChecked />*/}
        {/*    </div>*/}
        {/*  </Form.Item>*/}
        {/*)}*/}

        {isEdit && !isMobile && (
          <Form.Item name="delete">
            <span
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                cursor: "pointer",
              }}
              className="ant-dropdown-menu-title-content"
              onClick={() => setDeleteModalOpen(true)}
            >
              <Typography
                as="label"
                className="ant-dropdown-menu-title-content"
                style={{ color: "#990B0B", cursor: "pointer" }}
              >
                {t("deletePage")}
              </Typography>
              <img src={RemoveIcon} alt="remove" />
            </span>

            <DeleteModal
              title={t("deletePage")}
              text={t("approveDeletePage")}
              open={deleteModalOpen}
              setOpen={setDeleteModalOpen}
              onOk={handleDeleteStory}
            />
          </Form.Item>
        )}
      </Form>
    </AddPeopleContentWrapper>
  );
}

export default AddPeopleContent;
